
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const Blog: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // SEO optimization for blog listing page
  React.useEffect(() => {
    // Update page title for blog listing
    document.title = "Medical Interview Blog | NHS Interview Preparation Tips & Insights";

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Expert insights, strategies, and preparation tips for NHS medical interviews. Latest updates on interview frameworks, clinical governance, and career success.");
    }

    // Update Open Graph tags for blog listing
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const ogType = document.querySelector('meta[property="og:type"]');
    const ogImage = document.querySelector('meta[property="og:image"]');
    const ogSiteName = document.querySelector('meta[property="og:site_name"]');

    if (ogTitle) ogTitle.setAttribute('content', "Medical Interview Blog | NHS Interview Preparation Tips & Insights");
    if (ogDescription) ogDescription.setAttribute('content', "Expert insights, strategies, and preparation tips for NHS medical interviews. Latest updates on interview frameworks, clinical governance, and career success.");
    if (ogUrl) ogUrl.setAttribute('content', "https://nhsinterview.com/blog");
    if (ogType) ogType.setAttribute('content', 'website');
    if (ogImage) ogImage.setAttribute('content', 'https://nhsinterview.com/og-image.png');
    if (ogSiteName) ogSiteName.setAttribute('content', 'NHS Interview Preparation');

    // Update Twitter Card tags for blog listing
    const twitterCard = document.querySelector('meta[name="twitter:card"]');
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    const twitterImage = document.querySelector('meta[name="twitter:image"]');

    if (twitterCard) twitterCard.setAttribute('content', 'summary_large_image');
    if (twitterTitle) twitterTitle.setAttribute('content', "Medical Interview Blog | NHS Interview Preparation Tips & Insights");
    if (twitterDescription) twitterDescription.setAttribute('content', "Expert insights, strategies, and preparation tips for NHS medical interviews. Latest updates on interview frameworks, clinical governance, and career success.");
    if (twitterImage) twitterImage.setAttribute('content', 'https://nhsinterview.com/og-image.png');

    // Add canonical URL for blog listing
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', "https://nhsinterview.com/blog");

    // Add structured data for blog listing
    const existingScript = document.querySelector('script[data-blog-listing]');
    if (existingScript) {
      existingScript.remove();
    }

    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Blog",
          "name": "Medical Interview Blog",
          "description": "Expert insights, strategies, and preparation tips for NHS medical interviews. Latest updates on interview frameworks, clinical governance, and career success.",
          "url": "https://nhsinterview.com/blog",
          "inLanguage": "en-GB",
          "publisher": {
            "@type": "Organization",
            "name": "Medical Interview Team",
            "url": "https://nhsinterview.com",
            "logo": {
              "@type": "ImageObject",
              "url": "https://nhsinterview.com/logo.png"
            }
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": "https://nhsinterview.com/blog"
          },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://nhsinterview.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://nhsinterview.com/blog"
              }
            ]
          }
        },
        {
          "@type": "WebSite",
          "name": "NHS Interview Preparation",
          "url": "https://nhsinterview.com",
          "description": "AI-Powered Mock Interviews for Doctors - Master your NHS medical interview with AI",
          "inLanguage": "en-GB",
          "publisher": {
            "@type": "Organization",
            "name": "Medical Interview Team"
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://nhsinterview.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        },
        ...posts.map((post, index) => ({
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.excerpt,
          "url": `https://nhsinterview.com/blog/${post.slug}`,
          "datePublished": post.date,
          "dateModified": post.date,
          "author": {
            "@type": "Organization",
            "name": post.author,
            "url": "https://nhsinterview.com"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Medical Interview Team",
            "url": "https://nhsinterview.com"
          },
          "articleSection": post.category,
          "keywords": [post.category, post.tag, "NHS Interview", "Medical Interview"].join(", "),
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://nhsinterview.com/blog/${post.slug}`
          },
          "image": {
            "@type": "ImageObject",
            "url": "https://nhsinterview.com/og-image.png",
            "width": 1200,
            "height": 630
          },
          "position": index + 1
        }))
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-blog-listing', 'true');
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      const existingScript = document.querySelector('script[data-blog-listing]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const posts = [
    {
      id: 27,
      slug: 'portfolio-station-where-evidence-is-tested-not-just-shown',
      category: 'Interview Insights',
      title: 'Portfolio Station: Where Evidence Is Tested, Not Just Shown',
      excerpt: 'Many ST interview processes include a dedicated portfolio station where the panel reviews your documents, asks targeted questions, and assesses how convincingly your achievements stand up to scrutiny.',
      author: 'Medical Interview Team',
      date: 'Feb 22, 2025',
      readTime: '6 min read',
      tag: 'All Grades'
    },
    {
      id: 26,
      slug: 'how-frameworks-align-with-marking-schemes-positive-indicators',
      category: 'Interview Insights',
      title: 'How These Frameworks Align With Marking Schemes and Positive Indicators',
      excerpt: 'Frameworks are not simply communication aids. Their real power lies in how closely they align with how interviews are marked.',
      author: 'Medical Interview Team',
      date: 'Feb 20, 2025',
      readTime: '5 min read',
      tag: 'All Grades'
    },
    {
      id: 25,
      slug: 'spies-framework-handling-difficult-colleagues-high-risk-ethical-scenarios',
      category: 'Interview Insights',
      title: 'The SPIES Framework: Handling Difficult Colleagues and High-Risk Ethical Scenarios',
      excerpt: 'Questions about difficult colleagues are among the most emotionally challenging in medical interviews. They often involve hierarchy, conflict, and fear of repercussions.',
      author: 'Medical Interview Team',
      date: 'Feb 18, 2025',
      readTime: '6 min read',
      tag: 'All Grades'
    },
    {
      id: 24,
      slug: 'star-framework-turning-experience-into-scorable-evidence',
      category: 'Interview Insights',
      title: 'The STAR Framework: Turning Experience into Scorable Evidence',
      excerpt: 'Experience alone does not score marks at interview. What matters is how clearly you present that experience, how well you explain your reasoning.',
      author: 'Medical Interview Team',
      date: 'Feb 16, 2025',
      readTime: '7 min read',
      tag: 'All Grades'
    },
    {
      id: 23,
      slug: 'camp-framework-structuring-background-motivation-answers',
      category: 'Interview Insights',
      title: 'The CAMP Framework: Structuring Background and Motivation Answers Without Rambling',
      excerpt: 'Background and motivation questions are deceptively difficult. They feel open, familiar, and conversational — yet they are some of the most poorly answered questions.',
      author: 'Medical Interview Team',
      date: 'Feb 14, 2025',
      readTime: '5 min read',
      tag: 'All Grades'
    },
    {
      id: 22,
      slug: 'three-core-interview-frameworks-camp-star-spies',
      category: 'Interview Insights',
      title: 'The Three Core Interview Frameworks: When and Why to Use CAMP, STAR, and SPIES',
      excerpt: 'Once you understand why frameworks matter, the next step is knowing which framework to use, and when. Structured interviews are not random conversations.',
      author: 'Medical Interview Team',
      date: 'Feb 12, 2025',
      readTime: '6 min read',
      tag: 'All Grades'
    },
    {
      id: 21,
      slug: 'why-frameworks-matter-interviews',
      category: 'Interview Insights',
      title: 'Why Frameworks Matter in Interviews',
      excerpt: 'Medical interviews are not designed to reward improvisation. They reward clarity under pressure. When candidates struggle, it is rarely because they lack experience or insight.',
      author: 'Medical Interview Team',
      date: 'Feb 10, 2025',
      readTime: '5 min read',
      tag: 'All Grades'
    },
    {
      id: 20,
      slug: 'psychology-positivity-selling-what-you-have',
      category: 'Interview Insights',
      title: 'The Psychology of Positivity: Why Selling What You Have Matters',
      excerpt: 'Interviews are not neutral environments. They are evaluative, comparative, and time-limited. In this setting, the emotional tone of your answers matters more than many candidates realise.',
      author: 'Medical Interview Team',
      date: 'Feb 07, 2025',
      readTime: '4 min read',
      tag: 'All Grades'
    },
    {
      id: 19,
      slug: 'precision-over-padding-vagueness-detail',
      category: 'Interview Insights',
      title: 'Precision Over Padding: Avoiding Vagueness and Unnecessary Detail',
      excerpt: 'In interviews, more information does not automatically mean better answers. In fact, excessive detail often weakens otherwise strong responses.',
      author: 'Medical Interview Team',
      date: 'Feb 05, 2025',
      readTime: '4 min read',
      tag: 'All Grades'
    },
    {
      id: 18,
      slug: 'talking-about-yourself-without-arrogant',
      category: 'Interview Insights',
      title: 'Talking About Yourself Without Sounding Arrogant',
      excerpt: 'For many candidates, the hardest part of an interview is not answering clinical questions, but talking about themselves. Discomfort often leads to vague language.',
      author: 'Medical Interview Team',
      date: 'Feb 03, 2025',
      readTime: '4 min read',
      tag: 'All Grades'
    },
    {
      id: 17,
      slug: 'language-signals-confidence-power-words',
      category: 'Interview Insights',
      title: 'Language That Signals Confidence: Power Words, Active Verbs, and Ownership',
      excerpt: 'Confidence in interviews is communicated less by what you say than by how you say it. Subtle choices in language shape how interviewers perceive your competence.',
      author: 'Medical Interview Team',
      date: 'Feb 01, 2025',
      readTime: '5 min read',
      tag: 'All Grades'
    },
    {
      id: 16,
      slug: 'signposting-your-thinking-message',
      category: 'Interview Insights',
      title: 'Signposting Your Thinking: Making Your Message Impossible to Miss',
      excerpt: 'Many interview answers contain strong content but still fail to land. The issue is rarely what is said — it is how clearly the message is signposted.',
      author: 'Medical Interview Team',
      date: 'Jan 30, 2025',
      readTime: '5 min read',
      tag: 'All Grades'
    },
    {
      id: 15,
      slug: 'buzzwords-to-evidence-examples',
      category: 'Interview Insights',
      title: 'From Buzzwords to Evidence: Why Examples Separate Average from Excellent',
      excerpt: 'Most candidates know the right words to use in an interview. They describe themselves as motivated, hardworking, reflective, and good communicators.',
      author: 'Medical Interview Team',
      date: 'Jan 28, 2025',
      readTime: '5 min read',
      tag: 'All Grades'
    },
    {
      id: 14,
      slug: 'structure-not-style-frameworks',
      category: 'Interview Insights',
      title: 'Structure Is Not Style: How Clear Frameworks Help Interviewers Score You',
      excerpt: 'Many candidates believe that structure is about sounding polished. In reality, structure is about being understood and scorable.',
      author: 'Medical Interview Team',
      date: 'Jan 25, 2025',
      readTime: '5 min read',
      tag: 'All Grades'
    },
    {
      id: 13,
      slug: 'answer-question-asked-relevance',
      category: 'Interview Insights',
      title: 'Answer the Question You Were Asked: Relevance Over Knowledge',
      excerpt: 'One of the most common reasons strong candidates underperform at interview is not lack of knowledge, but failure to answer the question directly.',
      author: 'Medical Interview Team',
      date: 'Jan 22, 2025',
      readTime: '5 min read',
      tag: 'All Grades'
    },
    {
      id: 12,
      slug: 'why-time-matters-answer-length',
      category: 'Interview Insights',
      title: 'Why Time Matters: How Answer Length Influences Interview Scores',
      excerpt: 'Medical interviews are not won by saying more. They are won by saying enough — clearly, confidently, and within the limits of human attention.',
      author: 'Medical Interview Team',
      date: 'Jan 20, 2025',
      readTime: '6 min read',
      tag: 'All Grades'
    },
    {
      id: 11,
      slug: 'marking-scheme-explained',
      category: 'Interview Insights',
      title: 'The Marking Scheme Explained: How Interviewers Turn Behaviour Into Scores',
      excerpt: 'Candidates often leave interviews wondering how their answers were converted into numbers. The reality is that medical interviews use a behaviour-based marking scheme designed to translate complex human responses into structured, comparable scores.',
      author: 'Medical Interview Team',
      date: 'Jan 18, 2025',
      readTime: '8 min read',
      tag: 'All Grades'
    },
    {
      id: 10,
      slug: 'doctor-behind-answers',
      category: 'Interview Insights',
      title: 'The Doctor Behind the Answers: Personality, Values, and Professional Identity',
      excerpt: 'By the time candidates reach a medical interview, most can answer clinical questions competently. Guidelines are recalled, scenarios are managed safely, and structured responses are delivered with confidence.',
      author: 'Medical Interview Team',
      date: 'Jan 15, 2025',
      readTime: '7 min read',
      tag: 'All Grades'
    },
    {
      id: 9,
      slug: 'under-pressure-reveal',
      category: 'Interview Insights',
      title: 'Under Pressure: What Stress, Mistakes, and Uncertainty Reveal About You',
      excerpt: 'When candidates are asked about stress, mistakes, or moments of uncertainty, many assume these questions are designed to catch them out. In reality, these are among the most informative questions in a medical interview.',
      author: 'Medical Interview Team',
      date: 'Jan 12, 2025',
      readTime: '8 min read',
      tag: 'All Grades'
    },
    {
      id: 8,
      slug: 'attitude-over-expertise',
      category: 'Interview Insights',
      title: 'More Than Knowledge: Why Attitude Often Outweighs Expertise',
      excerpt: 'Many candidates approach medical interviews believing that knowledge is the main currency. They revise guidelines, rehearse clinical scenarios, and memorise definitions—yet still underperform.',
      author: 'Medical Interview Team',
      date: 'Jan 10, 2025',
      readTime: '6 min read',
      tag: 'All Grades'
    },
    {
      id: 7,
      slug: 'hidden-mark-scheme',
      category: 'Interview Insights',
      title: 'The Hidden Mark Scheme: How Interviewers Judge Your Answers',
      excerpt: 'Many candidates leave medical interviews convinced they "answered well" — yet are surprised by average or poor scores. This disconnect happens because interview performance is not judged subjectively.',
      author: 'Medical Interview Team',
      date: 'Jan 08, 2025',
      readTime: '7 min read',
      tag: 'All Grades'
    },
    {
      id: 6,
      slug: 'application-to-interview-room',
      category: 'Interview Insights',
      title: 'From Application to Interview Room: How You Are Assessed Before You Ever Speak',
      excerpt: 'Many candidates believe that medical interviews are won or lost in the interview room. In reality, a significant proportion of the assessment has already taken place long before you sit in front of a panel.',
      author: 'Medical Interview Team',
      date: 'Jan 05, 2025',
      readTime: '8 min read',
      tag: 'All Grades'
    },
    {
      id: 1,
      slug: 'interviewers-looking-for',
      category: 'Interview Insights',
      title: 'What Interviewers Are Really Looking For: The Three Core Selection Questions',
      excerpt: 'Medical interviews can feel overwhelming, but beneath the complexity lies a simple framework. Learn the three core questions that drive every selection decision.',
      author: 'Medical Interview Team',
      date: 'Jan 02, 2025',
      readTime: '6 min read',
      tag: 'All Grades'
    },
    {
      id: 2,
      slug: 'medical-interviews-structured-selection',
      category: 'Interview Insights',
      title: 'How Medical Interviews Really Work: Understanding Structured Selection',
      excerpt: 'Medical interviews have evolved from casual conversations to highly structured assessments. Learn the three fundamental questions behind every interview and how to prepare effectively.',
      author: 'Medical Interview Team',
      date: 'Dec 30, 2024',
      readTime: '8 min read',
      tag: 'All Grades'
    },
    {
      id: 2,
      slug: 'nhs-interview-structure',
      category: 'Interview Preparation',
      title: 'NHS Interview Structure: Stations, Formats, and What to Expect',
      excerpt: 'Master the NHS interview structure with detailed insights into formal and practical stations, from portfolio reviews to clinical governance and role-play scenarios.',
      author: 'Medical Interview Team',
      date: 'Dec 28, 2024',
      readTime: '10 min read',
      tag: 'All Grades'
    },
    {
      id: 2,
      slug: 'medical-interviews-transformed',
      category: 'Interview Insights',
      title: 'How Medical Interviews Have Transformed: From Conversation to Structured Assessment',
      excerpt: 'Medical interviews have evolved dramatically. What was once a conversational exchange is now a highly structured process requiring deliberate preparation and self-awareness.',
      author: 'Medical Interview Team',
      date: 'Dec 25, 2024',
      readTime: '7 min read',
      tag: 'All Grades'
    },
    {
      id: 3,
      slug: 'managing-the-interview',
      category: 'Interview Skills',
      title: 'Managing the Interview: Essential Techniques for NHS Success',
      excerpt: 'Master the art of interview management with proven techniques that will help you present yourself in the best possible light to NHS interview panels.',
      author: 'Medical Interview Team',
      date: 'Dec 22, 2024',
      readTime: '5 min read',
      tag: 'All Grades'
    }
  ];

  // Filter posts based on search term
  const filteredPosts = useMemo(() => {
    if (!searchTerm.trim()) return posts;

    const searchLower = searchTerm.toLowerCase();
    return posts.filter(post =>
      post.title.toLowerCase().includes(searchLower) ||
      post.excerpt.toLowerCase().includes(searchLower) ||
      post.category.toLowerCase().includes(searchLower) ||
      post.author.toLowerCase().includes(searchLower) ||
      post.tag.toLowerCase().includes(searchLower)
    );
  }, [searchTerm, posts]);

  return (
    <div className="min-h-screen bg-[#fdfdfe] relative overflow-hidden">
      {/* Background patterns */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none opacity-40">
        <div className="absolute top-[-10%] left-[20%] w-[800px] h-[800px] bg-blue-50 rounded-full blur-[120px]"></div>
      </div>

      <div className="container mx-auto px-6 py-20 relative z-10 max-w-6xl">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-8">
          <div className="animate-in fade-in slide-in-from-left-4 duration-700 md:flex-1">
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-blue-600 text-white px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest">Resource Hub</span>
              <h2 className="text-slate-400 font-bold text-sm">Medical Interview Insights</h2>
            </div>
            <h1 className="text-5xl font-black text-slate-900 tracking-tight leading-none">The Interview <span className="text-blue-600">Archive.</span></h1>
            <p className="text-slate-500 mt-4 max-w-lg font-medium italic">Latest strategies, legal updates, and framework guides for the 2024/25 NHS recruitment cycle.</p>
          </div>

          {/* Search Bar */}
          <div className="relative w-full md:w-auto md:max-w-md">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-12 py-3 bg-white border-2 border-slate-100 rounded-2xl outline-none focus:border-blue-500 focus:ring-0 transition-all text-slate-700 placeholder-slate-400"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                title="Clear search"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Search Results Info */}
        {searchTerm && (
          <div className="mb-8 text-center">
            <div className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full border border-blue-200">
              <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="text-blue-700 font-medium text-sm">
                Found <span className="font-bold">{filteredPosts.length}</span> article{filteredPosts.length !== 1 ? 's' : ''} for "<span className="font-bold">{searchTerm}</span>"
              </p>
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-8">
          <div></div>
          <button
            onClick={() => navigate('/')}
            className="group flex items-center gap-3 bg-white border-2 border-slate-100 px-8 py-4 rounded-[1.5rem] font-black text-xs uppercase tracking-widest hover:border-blue-600 hover:text-blue-600 transition-all shadow-sm active:scale-95"
          >
            <svg className="group-hover:translate-x-[-4px] transition-transform" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
            Return to Dashboard
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.length > 0 ? (
            filteredPosts.map((post, i) => (
            <article
              key={post.id}
              onClick={() => navigate(`/blog/${post.slug}`)}
              className={`p-6 bg-white rounded-[2rem] border border-slate-100 shadow-xl shadow-slate-100/50 hover:border-blue-200 transition-all group flex flex-col cursor-pointer animate-in fade-in slide-in-from-bottom-8 duration-700`}
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <div className="flex justify-between items-start mb-4">
                <span className="text-[9px] font-black text-blue-600 uppercase tracking-[0.2em] bg-blue-50 px-3 py-1 rounded-full">{post.category}</span>
                <span className="bg-slate-50 text-slate-400 text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter">{post.tag}</span>
              </div>

              <h3 className="text-xl font-black text-slate-900 mb-3 group-hover:text-blue-600 transition-colors leading-tight line-clamp-2">
                {post.title}
              </h3>

              <p className="text-slate-500 font-medium leading-relaxed mb-6 line-clamp-2 text-sm">
                {post.excerpt}
              </p>

              <div className="mt-auto pt-4 border-t border-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center font-black text-slate-400 text-xs">
                    {post.author.charAt(0)}
                  </div>
                  <div>
                    <p className="text-xs font-black text-slate-800 uppercase tracking-tight">{post.author}</p>
                    <p className="text-[9px] font-bold text-slate-400">{post.date}</p>
                  </div>
                </div>
                <span className="text-[9px] font-black text-blue-400 uppercase tracking-widest">{post.readTime}</span>
              </div>
            </article>
          ))
          ) : searchTerm ? (
            <div className="col-span-full text-center py-16">
              <div className="w-20 h-20 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-slate-700 mb-3">No articles found</h3>
              <p className="text-slate-500 mb-6 max-w-md mx-auto">We couldn't find any articles matching "<span className="font-semibold text-slate-700">{searchTerm}</span>". Try different keywords or browse all our articles.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => setSearchTerm('')}
                  className="bg-blue-600 text-white px-8 py-3 rounded-xl font-bold text-sm hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl active:scale-95"
                >
                  View All Articles
                </button>
                <button
                  onClick={() => setSearchTerm('')}
                  className="bg-white border-2 border-slate-200 text-slate-600 px-8 py-3 rounded-xl font-bold text-sm hover:border-slate-300 hover:bg-slate-50 transition-all"
                >
                  Clear Search
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {/* Newsletter / CTA Section */}
        <div className="mt-24 p-12 bg-slate-900 rounded-[4rem] text-white flex flex-col items-center text-center relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 blur-[100px] pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-600/10 blur-[100px] pointer-events-none"></div>
          
          <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-[0.4em] mb-4">Stay Competitive</h4>
          <h3 className="text-3xl font-black mb-6 tracking-tight">Get the Weekly "Panel Insider" Newsletter</h3>
          <p className="text-slate-400 max-w-md mb-10 font-medium italic">New station banks, legal precedents, and Trust-specific interview feedback delivered every Sunday.</p>
          
          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
            <input 
              type="email" 
              placeholder="Enter doctor email..." 
              className="flex-grow bg-white/5 border border-white/10 p-5 rounded-2xl outline-none focus:border-blue-600 transition-all font-bold text-sm"
            />
            <button className="bg-blue-600 px-8 py-5 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-blue-700 transition-all active:scale-95 shadow-xl shadow-blue-900/40">
              Subscribe
            </button>
          </div>
          <p className="mt-6 text-[9px] font-bold text-slate-500 uppercase tracking-widest">Trusted by 12,000+ UK Medical Professionals</p>
        </div>
      </div>
    </div>
  );
};

export default Blog;
